// Formatting utilities

export function formatNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

export function formatPercent(value: number, decimals = 1): string {
  return `${value >= 0 ? '+' : ''}${formatNumber(value, decimals)}%`;
}

export function formatLargeNumber(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
  return `$${value.toFixed(2)}`;
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

export function getSignalColor(signal: string): string {
  switch (signal) {
    case 'STRONG_BUY':
      return 'text-emerald-500';
    case 'BUY':
      return 'text-green-500';
    case 'HOLD':
      return 'text-yellow-500';
    case 'SELL':
      return 'text-orange-500';
    case 'STRONG_SELL':
      return 'text-red-500';
    default:
      return 'text-gray-500';
  }
}

export function getSignalBgColor(signal: string): string {
  switch (signal) {
    case 'STRONG_BUY':
      return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
    case 'BUY':
      return 'bg-green-500/10 text-green-500 border-green-500/20';
    case 'HOLD':
      return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    case 'SELL':
      return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
    case 'STRONG_SELL':
      return 'bg-red-500/10 text-red-500 border-red-500/20';
    default:
      return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  }
}

export function getScoreColor(score: number): string {
  if (score >= 70) return 'text-emerald-500';
  if (score >= 50) return 'text-green-500';
  if (score >= 30) return 'text-yellow-500';
  if (score >= 10) return 'text-orange-500';
  return 'text-red-500';
}

export function getSentimentLabel(score: number): string {
  // Score is 0-100 scale where 50 is neutral
  if (score >= 75) return 'Very Bullish';
  if (score >= 60) return 'Bullish';
  if (score >= 40) return 'Neutral';
  if (score >= 25) return 'Bearish';
  return 'Very Bearish';
}

export function formatSignal(signal: string): string {
  // Convert STRONG_BUY to "Strong Buy", SELL to "Sell", etc.
  return signal
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

export function capitalizeWord(word: string): string {
  // Capitalize first letter of a word: "loved" -> "Loved"
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

export function formatTierName(tier: string): string {
  // Format subscription tier for display: "early_supporter" -> "Early Supporter"
  if (!tier) return 'Free';
  return tier
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
